import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';
import { ReactComponent as ShowListIcon } from "assets/icons/new/UI/ShowList.svg";
import { ReactComponent as CloseIcon } from "assets/icons/new/UI/CloseCircleFilled.svg";
import { useAppSelector } from 'redux/hooks';
import { useDispatch } from 'react-redux';
import { setUserCurrency } from 'redux/users/actions';

// Типы для TypeScript
interface Language {
  icon: string;
}

interface Country {
  value: string;
  label: string;
  icon: string;
}

interface Currency {
  value: string;
  label: string;
}

interface ModalProps {
  onSave: (language: string, country: string, currency: string) => void;
}

const selectConfig = (placeholder: string) => ({
  placeholder,
  hasSearch: true,
  searchPlaceholder: placeholder,
  searchClasses: `block w-full sm:text-sm border !border-theme-border dark:!border-theme-border rounded-xl focus:outline-none focus:border-2 focus:ring-0`,
  searchWrapperClasses: "p-2 sticky top-0",
  toggleTag: "<button type=\"button\" aria-expanded=\"false\"><div data-icon></div></button>",
  toggleClasses: "hs-select-disabled:pointer-events-none hs-select-disabled:opacity-50 relative flex items-center gap-x-2 text-nowrap w-full cursor-pointer text-start text-sm focus:outline-hidden",
  dropdownClasses: `!visible mt-1 z-80 w-full max-h-72 space-y-0.5 z-50 overflow-hidden overflow-y-auto shadow-2xl border border-theme-border dark:border-theme-border bg-theme-bg dark:bg-theme-bg rounded-xl [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 dark:[&::-webkit-scrollbar-track]:bg-neutral-700 dark:[&::-webkit-scrollbar-thumb]:bg-neutral-500 fixed`,
  optionClasses: `p-2 w-full text-sm cursor-pointer hover:bg-theme-hover dark:hover:bg-theme-hover hover:text-theme-color-active dark:hover:text-theme-color-active focus:outline-hidden`,
  optionTemplate: "<div><div class=\"flex items-center gap-x-2\"><div data-icon></div><div className=\"overflow-hidden text-ellipsis max-w-full\" data-title></div><span class=\"hidden hs-selected:block ms-auto\"><svg class=\"shrink-0 size-3.5 \" xmlns=\"http:.w3.org/2000/svg\" width=\"24\" height=\"24\" viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" strokeWidth=\"2\" strokeLinecap=\"round\" strokeLinejoin=\"round\"><polyline points=\"20 6 9 17 4 12\"/></svg></span></div></div>",
  dropdownScope: "window",
});

export const normalizeLanguage = (lang: string): string => {
  const map: Record<string, string> = { 'ru': 'ru-RU', 'en': 'en-US' };
  return map[lang] ?? lang;
};

export const languagesIcon: Record<string, string> = {
  'en-US': `<svg class='shrink-0 size-4 rounded-full' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'><g fillRule='evenodd'><g strokeWidth='1pt'><path fill='#bd3d44' d='M0 0h247v10H0zm0 20h247v10H0zm0 20h247v10H0zm0 20h247v10H0zm0 20h247v10H0zm0 20h247v10H0zm0 20h247v10H0z' transform='scale(3.9385)'/><path fill='#fff' d='M0 10h247v10H0zm0 20h247v10H0zm0 20h247v10H0zm0 20h247v10H0zm0 20h247v10H0zm0 20h247v10H0z' transform='scale(3.9385)'/></g><path fill='#192f5d' d='M0 0h98.8v70H0z' transform='scale(3.9385)'/><path fill='#fff' d='M8.2 3l1 2.8H12L9.7 7.5l.9 2.7-2.4-1.7L6 10.2l.9-2.7-2.4-1.7h3zm16.5 0l.9 2.8h2.9l-2.4 1.7 1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7h2.9zm16.5 0l.9 2.8H45l-2.4 1.7 1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7h2.9zm16.4 0l1 2.8h2.8l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h3zm16.5 0l.9 2.8h2.9l-2.4 1.7 1 2.7L74 8.5l-2.3 1.7.9-2.7-2.4-1.7h2.9zm16.5 0l.9 2.8h2.9L92 7.5l1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7h2.9zm-74.1 7l.9 2.8h2.9l-2.4 1.7 1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7h2.9zm16.4 0l1 2.8h2.8l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h3zm16.5 0l.9 2.8h2.9l-2.4 1.7 1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7h2.9zm16.5 0l.9 2.8h2.9l-2.4 1.7 1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7H65zm16.4 0l1 2.8H86l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h3zm-74 7l.8 2.8h3l-2.4 1.7.9 2.7-2.4-1.7L6 24.2l.9-2.7-2.4-1.7h3zm16.4 0l.9 2.8h2.9l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h2.9zm16.5 0l.9 2.8H45l-2.4 1.7 1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7h2.9zm16.4 0l1 2.8h2.8l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h3zm16.5 0l.9 2.8h2.9l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h2.9zm16.5 0l.9 2.8h2.9L92 21.5l1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7h2.9zm-74.1 7l.9 2.8h2.9l-2.4 1.7 1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7h2.9zm16.4 0l1 2.8h2.8l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h3zm16.5 0l.9 2.8h2.9l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h2.9zm16.5 0l.9 2.8h2.9l-2.4 1.7 1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7H65zm16.4 0l1 2.8H86l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h3zm-74 7l.8 2.8h3l-2.4 1.7.9 2.7-2.4-1.7L6 38.2l.9-2.7-2.4-1.7h3zm16.4 0l.9 2.8h2.9l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h2.9zm16.5 0l.9 2.8H45l-2.4 1.7 1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7h2.9zm16.4 0l1 2.8h2.8l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h3zm16.5 0l.9 2.8h2.9l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h2.9zm16.5 0l.9 2.8h2.9L92 35.5l1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7h2.9zm-74.1 7l.9 2.8h2.9l-2.4 1.7 1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7h2.9zm16.4 0l1 2.8h2.8l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h3zm16.5 0l.9 2.8h2.9l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h2.9zm16.5 0l.9 2.8h2.9l-2.4 1.7 1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7H65zm16.4 0l1 2.8H86l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h3zm-74 7l.8 2.8h3l-2.4 1.7.9 2.7-2.4-1.7L6 52.2l.9-2.7-2.4-1.7h3zm16.4 0l.9 2.8h2.9l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h2.9zm16.5 0l.9 2.8H45l-2.4 1.7 1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7h2.9zm16.4 0l1 2.8h2.8l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h3zm16.5 0l.9 2.8h2.9l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h2.9zm16.5 0l.9 2.8h2.9L92 49.5l1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7h2.9zm-74.1 7l.9 2.8h2.9l-2.4 1.7 1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7h2.9zm16.4 0l1 2.8h2.8l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h3zm16.5 0l.9 2.8h2.9l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h2.9zm16.5 0l.9 2.8h2.9l-2.4 1.7 1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7H65zm16.4 0l1 2.8H86l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h3zm-74 7l.8 2.8h3l-2.4 1.7.9 2.7-2.4-1.7L6 66.2l.9-2.7-2.4-1.7h3zm16.4 0l.9 2.8h2.9l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h2.9zm16.5 0l.9 2.8H45l-2.4 1.7 1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7h2.9zm16.4 0l1 2.8h2.8l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h3zm16.5 0l.9 2.8h2.9l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h2.9zm16.5 0l.9 2.8h2.9L92 63.5l1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7h2.9z' transform='scale(3.9385)'/></g></svg>`,
  'ru-RU': `<svg class='shrink-0 size-4 rounded-full' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'><path fill='#fff' d='M0 0h512v170.7H0z'/><path fill='#0039a6' d='M0 170.7h512v170.6H0z'/><path fill='#d52b1e' d='M0 341.3h512V512H0z'/></svg>`,
  'zh': `<svg  class='shrink-0 size-4 rounded-full' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 36 24'><rect width='36' height='24' fill='#DE2910'/><path d='M9 6L12 12L9 18L6 12L9 6Z' fill='#FFDE00'/><circle cx='9' cy='12' r='2' fill='#FFDE00'/></svg>`,
  'tt': `<svg class='shrink-0 size-4 rounded-full' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 36 24'><rect width='36' height='8' fill='white'/><rect width='36' height='8' y='8' fill='#009246'/><rect width='36' height='8' y='16' fill='#C1272D'/></svg>`,
  'es': `<svg class='shrink-0 size-4 rounded-full' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 36 24'><rect width='36' height='8' fill='#C60B1E'/><rect width='36' height='8' y='8' fill='#FFC400'/><rect width='36' height='8' y='16' fill='#C60B1E'/></svg>`,
  'fr': `<svg class='shrink-0 size-4 rounded-full' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 36 24'><rect width='12' height='24' fill='#002395'/><rect x='12' width='12' height='24' fill='white'/><rect x='24' width='12' height='24' fill='#ED2939'/></svg>`,
};

export const languages = [
  {
    id: 0,
    period: 'Русский',
    language: "ru-RU"
  },
  {
    id: 1,
    period: 'English',
    language: "en-US"
  },
  {
    id: 2,
    period: '中文',
    language: "zh"
  },
  {
    id: 3,
    period: 'Татар',
    language: "tt"
  },
  {
    id: 4,
    period: 'Español',
    language: "es"
  },
  {
    id: 5,
    period: 'Français',
    language: "fr"
  }
]

// Данные для стран (сокращенный список)
const countries: Country[] = [
  { value: 'en', label: 'United States', icon: `<svg class='inline-block size-4 rounded-full' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'><g fillRule='evenodd'><g strokeWidth='1pt'><path fill='#bd3d44' d='M0 0h247v10H0zm0 20h247v10H0zm0 20h247v10H0zm0 20h247v10H0zm0 20h247v10H0zm0 20h247v10H0zm0 20h247v10H0z' transform='scale(3.9385)'/><path fill='#fff' d='M0 10h247v10H0zm0 20h247v10H0zm0 20h247v10H0zm0 20h247v10H0zm0 20h247v10H0zm0 20h247v10H0z' transform='scale(3.9385)'/></g><path fill='#192f5d' d='M0 0h98.8v70H0z' transform='scale(3.9385)'/><path fill='#fff' d='M8.2 3l1 2.8H12L9.7 7.5l.9 2.7-2.4-1.7L6 10.2l.9-2.7-2.4-1.7h3zm16.5 0l.9 2.8h2.9l-2.4 1.7 1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7h2.9zm16.5 0l.9 2.8H45l-2.4 1.7 1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7h2.9zm16.4 0l1 2.8h2.8l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h3zm16.5 0l.9 2.8h2.9l-2.4 1.7 1 2.7L74 8.5l-2.3 1.7.9-2.7-2.4-1.7h2.9zm16.5 0l.9 2.8h2.9L92 7.5l1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7h2.9zm-74.1 7l.9 2.8h2.9l-2.4 1.7 1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7h2.9zm16.4 0l1 2.8h2.8l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h3zm16.5 0l.9 2.8h2.9l-2.4 1.7 1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7h2.9zm16.5 0l.9 2.8h2.9l-2.4 1.7 1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7H65zm16.4 0l1 2.8H86l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h3zm-74 7l.8 2.8h3l-2.4 1.7.9 2.7-2.4-1.7L6 24.2l.9-2.7-2.4-1.7h3zm16.4 0l.9 2.8h2.9l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h2.9zm16.5 0l.9 2.8H45l-2.4 1.7 1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7h2.9zm16.4 0l1 2.8h2.8l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h3zm16.5 0l.9 2.8h2.9l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h2.9zm16.5 0l.9 2.8h2.9L92 21.5l1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7h2.9zm-74.1 7l.9 2.8h2.9l-2.4 1.7 1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7h2.9zm16.4 0l1 2.8h2.8l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h3zm16.5 0l.9 2.8h2.9l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h2.9zm16.5 0l.9 2.8h2.9l-2.4 1.7 1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7H65zm16.4 0l1 2.8H86l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h3zm-74 7l.8 2.8h3l-2.4 1.7.9 2.7-2.4-1.7L6 38.2l.9-2.7-2.4-1.7h3zm16.4 0l.9 2.8h2.9l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h2.9zm16.5 0l.9 2.8H45l-2.4 1.7 1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7h2.9zm16.4 0l1 2.8h2.8l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h3zm16.5 0l.9 2.8h2.9l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h2.9zm16.5 0l.9 2.8h2.9L92 35.5l1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7h2.9zm-74.1 7l.9 2.8h2.9l-2.4 1.7 1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7h2.9zm16.4 0l1 2.8h2.8l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h3zm16.5 0l.9 2.8h2.9l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h2.9zm16.5 0l.9 2.8h2.9l-2.4 1.7 1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7H65zm16.4 0l1 2.8H86l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h3zm-74 7l.8 2.8h3l-2.4 1.7.9 2.7-2.4-1.7L6 52.2l.9-2.7-2.4-1.7h3zm16.4 0l.9 2.8h2.9l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h2.9zm16.5 0l.9 2.8H45l-2.4 1.7 1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7h2.9zm16.4 0l1 2.8h2.8l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h3zm16.5 0l.9 2.8h2.9l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h2.9zm16.5 0l.9 2.8h2.9L92 49.5l1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7h2.9zm-74.1 7l.9 2.8h2.9l-2.4 1.7 1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7h2.9zm16.4 0l1 2.8h2.8l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h3zm16.5 0l.9 2.8h2.9l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h2.9zm16.5 0l.9 2.8h2.9l-2.4 1.7 1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7H65zm16.4 0l1 2.8H86l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h3zm-74 7l.8 2.8h3l-2.4 1.7.9 2.7-2.4-1.7L6 66.2l.9-2.7-2.4-1.7h3zm16.4 0l.9 2.8h2.9l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h2.9zm16.5 0l.9 2.8H45l-2.4 1.7 1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7h2.9zm16.4 0l1 2.8h2.8l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h3zm16.5 0l.9 2.8h2.9l-2.3 1.7.9 2.7-2.4-1.7-2.3 1.7.9-2.7-2.4-1.7h2.9zm16.5 0l.9 2.8h2.9L92 63.5l1 2.7-2.4-1.7-2.4 1.7 1-2.7-2.4-1.7h2.9z' transform='scale(3.9385)'/></g></svg>` },
  { value: 'ru', label: 'Russia', icon: `<svg class='inline-block size-4 rounded-full' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'><path fill='#fff' d='M0 0h512v170.7H0z'/><path fill='#0039a6' d='M0 170.7h512v170.6H0z'/><path fill='#d52b1e' d='M0 341.3h512V512H0z'/></svg>` },
];

// Данные для валют
const currencies: Currency[] = [
  { value: 'usd', label: 'USD' },
  { value: 'ru', label: 'RU' },
];

export const LanguageCurrencyModal: React.FC<ModalProps> = ({ onSave }) => {
  const { t, i18n } = useTranslation();
  const [selectedLanguage, setSelectedLanguage] = useState<string>(i18n.language);
  const [selectedCountry, setSelectedCountry] = useState<string>('US');
  const [selectedCurrency, setSelectedCurrency] = useState<string>('USD');
  const modalRef = useRef<HTMLDivElement>(null);


  const handleSave = () => {
    onSave(selectedLanguage, selectedCountry, selectedCurrency);
    handleClose();
  };

  const handleClose = () => {
    const trigger = document.getElementById('regionalSettingsModal-backdrop');
    if (trigger) { trigger.click(); }
  }

  // Оригинальный механизм: new HSOverlay на каждый клик гарантирует что
  // при следующем открытии всегда есть живой экземпляр (Preline уничтожает
  // экземпляр при close, поэтому нужно пересоздавать)
  useEffect(() => {
    const initModal = () => {
      if (modalRef.current && (window as any).HSOverlay) {
        new (window as any).HSOverlay(modalRef.current, {
          portal: true,
          backdrop: true,
          keyboard: true,
          backdropClass: 'bg-black bg-opacity-50'
        });
      }
    };
    const handleClick = () => { setTimeout(initModal, 50); };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  // Фикс накопления backdrop-ов: при открытии модала оставляем только один
  useEffect(() => {
    const el = modalRef.current;
    if (!el) return;
    const observer = new MutationObserver(() => {
      if (!el.classList.contains('hidden')) {
        // Оставляем последний backdrop (наиболее свежий экземпляр), убираем остальные
        const backdrops = document.querySelectorAll('#regionalSettingsModal-backdrop');
        if (backdrops.length > 1) {
          Array.from(backdrops).slice(0, -1).forEach(b => {
            (b.closest('[data-hs-overlay-backdrop-container]') ?? b).remove();
          });
        }
      }
    });
    observer.observe(el, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return (
    <div
      id="regionalSettingsModal"
      ref={modalRef}
      className="hs-overlay hidden size-full fixed z-[9999]"
      data-hs-overlay-auto-close="true"
      data-hs-overlay-portal="true"
      style={{ zIndex: 9999 }}
      onClick={(e) => {
        if (e.target === modalRef.current) {
          handleClose();
        }
      }}
    >
      <div
        className={`sm:max-w-xl sm:w-full m-3 sm:mx-auto top-1/2 left-1/2 transform translate-y-1/4 flex flex-col shadow-2xl border border-theme-border dark:border-theme-border bg-theme-bg dark:bg-theme-bg dark:text-white rounded-xl`}
      >
        <div className={`py-4 px-6 flex justify-between items-center border-b border-theme-border dark:border-theme-border`}>
          <h3 id="hs-pro-shmnrsm-label" className="font-medium">
            {t('Regional Settings')}
          </h3>
          <button
            type="button"
            className={`size-8 shrink-0 flex justify-center items-center gap-x-2 rounded-full hover:text-theme-hover dark:hover:text-theme-hover`}
            aria-label="Close"
            onClick={handleClose}
          >
            <span className="sr-only">Close</span>
            <CloseIcon width="24" height="24" stroke="currentColor" className="shrink-0"/>
          </button>
        </div>
        <div className="overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 dark:[&::-webkit-scrollbar-track]:bg-neutral-700 dark:[&::-webkit-scrollbar-thumb]:bg-neutral-500">
          <div className="p-6 space-y-5">
            <div>
              <span className="block mb-2 text-sm font-medium">
                {t('Language')}
              </span>
              <div className={`relative hover:text-theme-hover dark:hover:text-theme-hover`}>
                <select
                  data-hs-select={JSON.stringify(selectConfig(t('Select Language')))}
                  className="hidden"
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                >
                  <option value="">Choose</option>
                  {languages.map((lang, i) => (
                    <option key={lang.id} value={lang.language} selected={lang.language === selectedLanguage} data-hs-select-option={`{"icon": "${languagesIcon[lang.language]}"}`}>
                      {t(lang.period)}
                    </option>
                  ))}
                </select>

                <div className="absolute top-1/2 end-2.5 -translate-y-1/2">
                  <ShowListIcon width="14" height="14" fill="currentColor" className="shrink-0" />
                </div>
              </div>
            </div>
            <div>
              <span className="block mb-2 text-sm font-medium">
                {t('Country')}
              </span>
              <div className={`relative hover:text-theme-hover dark:hover:text-theme-hover`}>
                <select
                  data-hs-select={JSON.stringify(selectConfig(t('Select Country')))}
                  className="hidden"
                  onChange={(e) => setSelectedCountry(e.target.value)}
                >
                  <option value="">Choose</option>
                  {countries.map((country, i) => (
                    <option key={country.value} value={country.value} selected={country.value === selectedCountry} data-hs-select-option={`{"icon": "${country.icon}"}`}>
                      {country.label}
                    </option>
                  ))}
                </select>

                <div className="absolute top-1/2 end-2.5 -translate-y-1/2">
                  <ShowListIcon width="14" height="14" fill="currentColor" className="shrink-0" />
                </div>
              </div>
            </div>
            <div>
              <span className="block mb-2 text-sm font-medium">
                {t('Currency')}
              </span>
              <div className={`relative hover:text-theme-hover dark:hover:text-theme-hover`}>
                <select
                  data-hs-select={JSON.stringify(selectConfig(t('Select currency')))}
                  className="hidden"
                  onChange={(e) => setSelectedCurrency(e.target.value)}
                >
                  <option value="">Choose</option>
                  {currencies.map((currency, i) => (
                    <option key={currency.value} value={currency.value} selected={currency.value === selectedCurrency}>
                      {currency.label}
                    </option>
                  ))}
                </select>

                <div className="absolute top-1/2 end-2.5 -translate-y-1/2">
                  <ShowListIcon width="14" height="14" fill="currentColor" className="shrink-0" />
                </div>
              </div>
            </div>

            <p className="text-[13px]">
              <Trans i18nKey='RegionalSettingsMessage'>
                By continuing, you accept the <a className="underline-offset-4 underline focus:outline-hidden" href="#">
                  Terms and Conditions
                </a> of your chosen country or region.
              </Trans>
            </p>
          </div>
        </div>
        <div className="p-6 pt-2 md:pt-2 flex flex-col gap-2">
          <button
            type="button"
            className={`py-2.5 px-4 w-full inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg bg-theme-bg-active dark:bg-theme-bg-active text-theme-color-active dark:text-theme-color-active`}
            onClick={handleSave}
          >
            {t('Accept')}
          </button>

          <div className="text-center">
            <button
              type="button"
              className={`py-2.5 px-4 w-full inline-flex justify-center items-center gap-x-1.5 text-sm font-medium rounded-lg border border-theme-border dark:border-theme-border shadow-2xs focus:outline-hidden`}
              onClick={handleClose}
            >
              {t('Cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const RegionalSettings: React.FC = () => {
  const { t, i18n } = useTranslation();

  const { currentCurrency } = useAppSelector(({ users }) => ({
    currentCurrency: users.currentUser.currency,
  }))

  return (
    <button
      type="button"
      className="grid grid-cols-2 gap-2 items-center w-full"
      data-hs-overlay="#regionalSettingsModal"
      aria-expanded="false"
    >
      <div className={`flex items-center justify-between hover:text-theme-hover dark:hover:text-theme-hover`}>
        <span className='text-sm truncate'>{t(i18n.language)}</span>
        <div>
          <ShowListIcon width="14" height="14" fill="currentColor" className="shrink-0" />
        </div>
      </div>
      <div className={`flex items-center justify-between hover:text-theme-hover dark:hover:text-theme-hover`}>
        <span className='shrink-0 size-4 rounded-full' dangerouslySetInnerHTML={{ __html: languagesIcon[i18n.language] }}/>
        <div>{currentCurrency}</div>
      </div>
    </button>
  );
};

export default RegionalSettings;